import { Alert, Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, ListSubheader, MenuItem, OutlinedInput, Radio, RadioGroup, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import FileUploadAllLineService from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import LocationService from '../../../../shared/services/location/location.service'
import { ICountry } from '../../../../shared/services/location/location.model'
import { ILocation } from '../../../../shared/services/location/location.model'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import { SupplierPicker } from '../../../presentation-components/form-field-components/supplier-picker'
import VehicleService from '../../../../shared/services/vehicle/vehicle.service'
import { IVehicleDetails } from '../../../../shared/services/vehicle/vehicle.model'
import ProjectService from '../../../../shared/services/project/project.service'
import { IProject } from '../../../../shared/services/project/project.model'
import DataTypeService from '../../../../shared/services/data-types/data-types.service'
import { IDataTypeRelation } from '../../../../shared/services/data-types/data-types.model'
import RegulatedEmissionTradingSchemeService from '../../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.service'
import { IRegulatedEmissionTradingScheme } from '../../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.model'
import GroupService from '../../../../shared/services/group/group.service'
import { IGroupWithCategory } from '../../../../shared/services/group/group.model'
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category'
import CategoryService from '../../../../shared/services/category/category.service'
import { ICategory } from '../../../../shared/services/category/category.model'
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service'
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model'
import UserDetailsService from '../../../../shared/services/user-details/user-details.service'
import { IUserDetails } from '../../../../shared/services/user-details/user-details.model'
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model';
import RuleService from '../../../../shared/services/rule/rule.service';
import { IRule } from '../../../../shared/services/rule/rule.model';
import { EmissionsFilterLogic } from '../../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic';
import EmissionService from '../../../../shared/services/emission/emission.service';
import EmissionCalculationService from '../../../../shared/services/emission-calculation/emission-calculation.service';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { FLAG_CATEGORY_OPTIONS } from '../../../../shared/constants/flag-emissions';
import { useFlagEmissionsCapability } from '../../../../hooks/use-flag-emissions-capability';
import { formatConversionCodeLabel, sortByConversionCode } from '../../../../shared/utils/conversion-utils';
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils';

export const EditBulkUploadLinesDialog: FunctionComponent<{
  open: boolean,
  dialogDetailsIds: Array<string>,
  closeHandler: () => void,
  successHandler: () => void,
  fileType?: string,
  showMenuItems: string[],
  reportPeriodId?: string,
  create?: boolean,
  isForTransactions?: boolean,
  calculationEditCodes?: string[],
  isEditAll?: boolean,
  queryString?: string,
  fileUploadId?: string,
}> = ({
  open,
  closeHandler,
  dialogDetailsIds,
  successHandler,
  fileType,
  showMenuItems,
  reportPeriodId,
  create,
  isForTransactions = false,
  calculationEditCodes = [],
  isEditAll = false,
  queryString,
  fileUploadId,
}) => {
    //Services
    const fileUploadAllLineService = new FileUploadAllLineService()
    const emissionService = new EmissionService()
    const subsidiaryService = SubsidiaryService.getInstance()
    const locationService = LocationService.getInstance();
    const contactSupplierService = ContactSupplierService.getInstance();
    const vehicleService = VehicleService.getInstance();
    const projectService = ProjectService.getInstance()
    const dataTypeService = new DataTypeService()
    const regulatedEmissionTradingSchemeService = new RegulatedEmissionTradingSchemeService()
    const groupService = new GroupService()
    const categoryService = new CategoryService()
    const capitalGoodService = new CapitalGoodService()
    const userDetailsService = new UserDetailsService()
    const spendBasedConversionService = new SpendBasedConversionService();
    const conversionSourceService = new ConversionSourceService();
    const ruleService = new RuleService();
    const emissionCalculationService = new EmissionCalculationService();
    const customFieldService = new CustomFieldService();
    //Context
    const userNotificationsContext = useContext(UserNotificationsContext)
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const reportPeriodContext = useContext(ReportPeriodContext)
    const organisation = userDetailsContext.userDetails?.organisation
    //State
    const [editCodes, setEditCodes] = useState<string[]>([])
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [countries, setCountries] = useState<ICountry[]>()
    const [locations, setLocations] = useState<ILocation[]>()
    const [suppliers, setSuppliers] = useState<IContactSupplierDetails[]>()
    const [vehicles, setVehicles] = useState<IVehicleDetails[]>()
    const [projects, setProjects] = useState<IProject[]>()
    const [dataTypes, setDataTypes] = useState<IDataTypeRelation[]>()
    const [regulatedEmissionTradingSchemes, setRegulatedEmissionTradingSchemes] = useState<IRegulatedEmissionTradingScheme[]>()
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [categories, setCategories] = useState<ICategory[]>()
    const [capitalGoods, setCapitalGoods] = useState<ICapitalGood[]>()
    const [users, setUsers] = useState<IUserDetails[]>()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [openStartDate, setOpenStartDate] = useState(false)
    const [openEndDate, setOpenEndDate] = useState(false)
    const [openInvoiceDate, setOpenInvoiceDate] = useState(false)
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()
    const sortedSpendBasedConversions = useMemo(
      () => sortByConversionCode(spendBasedConversions),
      [spendBasedConversions],
    )
    const [rules, setRules] = useState<IRule[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>([])
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    const { flagEnabled } = useFlagEmissionsCapability()
    const selectedCalculationEditCodes = editCodes.filter(editCode => calculationEditCodes.includes(editCode))
    const isBulkCalculationEdit = isForTransactions && selectedCalculationEditCodes.length > 0
    // FLAG is stored on the calculations but is metadata only (no recalculation), so unlike the
    // category/factor edit codes it goes to its own endpoint and can be combined with emission fields.
    const canEditFlagClassification = isForTransactions && flagEnabled
    // Custom field editing (emission/transaction context only). Each custom field is
    // offered as a selectable edit code: `EDIT_CF::<customFieldId>`.
    const CUSTOM_FIELD_CODE_PREFIX = 'EDIT_CF::'
    const customFieldIdFromCode = (code: string) => code.startsWith(CUSTOM_FIELD_CODE_PREFIX) ? code.slice(CUSTOM_FIELD_CODE_PREFIX.length) : null
    const visibleCustomFields = isForTransactions ? customFields.filter(cf => (cf.displayMode ?? 'optional') !== 'hidden') : []
    const isCustomFieldMandatory = (cf: ICustomField) => (cf.displayMode ?? 'optional') === 'mandatory'

    useEffect(() => {
      if (showMenuItems.includes('EDIT_SUBSIDIARY')) {
        subsidiaryService.getAll()
          .then(response => setSubsidiaries(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_COUNTRY')) {
        locationService.getAllCountries()
          .then(response => setCountries(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_LOCATION')) {
        locationService.getActiveLocations()
          .then(response => setLocations(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_SUPPLIER')) {
        contactSupplierService.getActiveSuppliersLite(false)
          .then(response => setSuppliers(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_VEHICLE')) {
        vehicleService.getAllActiveVehicles()
          .then(response => setVehicles(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_PROJECT')) {
        projectService.getAllActive()
          .then(response => setProjects(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_DATA_TYPE')) {
        dataTypeService.getAll()
          .then(response => setDataTypes(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_REGULATED_EMISSION_TRADING_SCHEME')) {
        regulatedEmissionTradingSchemeService.getAll()
          .then(response => setRegulatedEmissionTradingSchemes(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_GROUP') || showMenuItems.includes('EDIT_CONVERSION')) {
        groupService.getGroupsWithCategories(false)
          .then(response => setGroups(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_CATEGORY') || showMenuItems.includes('EDIT_CONVERSION')) {
        categoryService.getAllCategories()
          .then(response => setCategories(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_PRODUCT')) {
        capitalGoodService.getAllActive()
          .then(response => setCapitalGoods(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_ASSIGNED_USER')) {
        userDetailsService.getAllUserDetails('?status=active')
          .then((response) => {
            setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
          })
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_RULE')) {
        ruleService.getAll(`type=emission-activity`)
          .then(response => setRules(response))
          .catch(err => console.log(err))
      }

      if (showMenuItems.includes('EDIT_ACTIVITY') || showMenuItems.includes('EDIT_CONVERSION')) {
        conversionSourceService.getAll(true)
          .then(response => setConversionSources(response))
          .catch(err => console.log(err))
      }
    }, [showMenuItems])

    useEffect(() => {
      if (!open || !isForTransactions) return
      customFieldService.getAllActive('&entryType=transaction')
        .then(response => setCustomFields(response))
        .catch(err => console.log(err))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isForTransactions])

    const fieldConfig = organisation?.emissionFieldConfig
    const editCodeToFieldKey: Record<string, string> = {
      EDIT_COUNTRY: 'country',
      EDIT_LOCATION: 'location',
      EDIT_SUPPLIER: 'supplier',
      EDIT_VEHICLE: 'vehicle',
      EDIT_PRODUCT: 'product',
      EDIT_PROJECT: 'project',
      EDIT_REGULATED_EMISSION_TRADING_SCHEME: 'ets',
      EDIT_EXCLUDE_FROM_REPORTING: 'excludeFromReporting',
    }
    const isFieldHidden = (editCode: string) => {
      if (!isForTransactions) return false
      const key = editCodeToFieldKey[editCode]
      if (!key) return false
      return fieldConfig?.[key as keyof typeof fieldConfig] === 'hidden'
    }
    const isFieldMandatory = (editCode: string) => {
      if (!isForTransactions) return false
      const key = editCodeToFieldKey[editCode]
      if (!key) return false
      return fieldConfig?.[key as keyof typeof fieldConfig] === 'mandatory'
    }

    const editUploadLineBulkSchema = useMemo(() => {
      const customFieldShape: Record<string, Yup.AnySchema> = {}
      visibleCustomFields.forEach(cf => {
        if (editCodes.includes(`${CUSTOM_FIELD_CODE_PREFIX}${cf.id}`)) {
          customFieldShape[cf.id] = isCustomFieldMandatory(cf)
            ? Yup.string().required(`${cf.fieldName} is required`)
            : Yup.string().nullable()
        }
      })
      return Yup.object().shape({
      subsidiaryId: editCodes.includes('EDIT_SUBSIDIARY') ? Yup.string().nullable() : Yup.string().nullable(),
      countryId: editCodes.includes('EDIT_COUNTRY') && isFieldMandatory('EDIT_COUNTRY')
        ? Yup.string().required('Country is required') : Yup.string().nullable(),
      locationId: editCodes.includes('EDIT_LOCATION') && isFieldMandatory('EDIT_LOCATION')
        ? Yup.string().required('Location is required') : Yup.string().nullable(),
      contactSupplierId: editCodes.includes('EDIT_SUPPLIER')
        ? (isFieldMandatory('EDIT_SUPPLIER') || fileType === 'spend-based')
          ? Yup.string().required('Supplier is required')
          : Yup.string().nullable()
        : Yup.string().nullable(),
      vehicleId: editCodes.includes('EDIT_VEHICLE') && isFieldMandatory('EDIT_VEHICLE')
        ? Yup.string().required('Vehicle is required') : Yup.string().nullable(),
      projectId: editCodes.includes('EDIT_PROJECT') && isFieldMandatory('EDIT_PROJECT')
        ? Yup.string().required('Project is required') : Yup.string().nullable(),
      dataTypeId: editCodes.includes('EDIT_DATA_TYPE') ? Yup.string().required('Required') : Yup.string().nullable(),
      regulatedEmissionTradingSchemeId: editCodes.includes('EDIT_REGULATED_EMISSION_TRADING_SCHEME') ? Yup.string().nullable() : Yup.string().nullable(),
      groupId: (editCodes.includes('EDIT_GROUP') || editCodes.includes('EDIT_CONVERSION')) ? Yup.string().required('Required') : Yup.string().nullable(),
      categoryId: (editCodes.includes('EDIT_CATEGORY') || editCodes.includes('EDIT_CONVERSION')) ? Yup.string().required('Required') : Yup.string().nullable(),
      unitsConsumed: editCodes.includes('EDIT_UNITS_CONSUMED') ? Yup.number().min(0.01, 'The amount must be greater than 0').required('Required') : Yup.number().nullable(),
      energyKwh: editCodes.includes('EDIT_ENERGY_KWH') ? Yup.number().min(0.0001, 'The energy consumption must be greater than 0').nullable() : Yup.number().nullable(),
      // 0 is a valid rate (a 100% renewable tariff), and the import template accepts it.
      customMarketBasedMultiplier: editCodes.includes('EDIT_SUPPLIER_EMISSION_RATE') ? Yup.number().min(0, 'The supplier emission rate cannot be negative').nullable() : Yup.number().nullable(),
      startDate: editCodes.includes('EDIT_START_DATE') ? Yup.string().required('Required') : Yup.string().nullable(),
      endDate: editCodes.includes('EDIT_END_DATE') ? Yup.string().required('Required') : Yup.string().nullable(),
      capitalGoodId: editCodes.includes('EDIT_PRODUCT') ? Yup.string().required('Required') : Yup.string().nullable(),
      assignedUserId: editCodes.includes('EDIT_ASSIGNED_USER') ? Yup.string().nullable() : Yup.string().nullable(),
      invoiceDate: editCodes.includes('EDIT_INVOICE_DATE') ? Yup.string().required('Required') : Yup.string().nullable(),
      total: editCodes.includes('EDIT_TOTAL') ? Yup.number().min(0.01, 'The amount must be greater than 0').required('Required') : Yup.number().nullable(),
      bankAccountName: editCodes.includes('EDIT_BANK_ACCOUNT_NAME') ? Yup.string().nullable() : Yup.string().nullable(),
      conversionSourceId: (editCodes.includes('EDIT_ACTIVITY') || editCodes.includes('EDIT_CONVERSION')) ? Yup.string().required('Required') : Yup.string().nullable(),
      measurementUnitId: editCodes.includes('EDIT_CONVERSION') && calculationEditCodes.includes('EDIT_CONVERSION') ? Yup.string().required('Required') : Yup.string().nullable(),
      returnId: (editCodes.includes('EDIT_ACTIVITY') || (editCodes.includes('EDIT_CONVERSION') && !calculationEditCodes.includes('EDIT_CONVERSION'))) ? Yup.string().required('Required') : Yup.string().nullable(),
      ruleId: editCodes.includes('EDIT_RULE') ? Yup.string().required('Required') : Yup.string().nullable(),
      excludeFromReporting: editCodes.includes('EDIT_EXCLUDE_FROM_REPORTING') ? Yup.boolean() : Yup.boolean().nullable(),
      excludeFromReportingReason: editCodes.includes('EDIT_EXCLUDE_FROM_REPORTING')
        ? Yup.string().when('excludeFromReporting', {
          is: true,
          then: schema => schema.required('You must provide a reason for exclusion'),
          otherwise: schema => schema.nullable().notRequired()
        })
        : Yup.string().nullable(),
      isFlag: editCodes.includes('EDIT_FLAG_CLASSIFICATION') ? Yup.boolean() : Yup.boolean().nullable(),
      flagCategory: Yup.string().nullable(),
      ...customFieldShape,
    })
    }, [editCodes, fileType, calculationEditCodes, fieldConfig, isForTransactions, customFields])

    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: '',
        countryId: '',
        locationId: '',
        contactSupplierId: '',
        vehicleId: '',
        projectId: '',
        dataTypeId: '',
        regulatedEmissionTradingSchemeId: '',
        groupId: '',
        categoryId: '',
        categoryName: '',
        detailLevelOne: '',
        detailLevelTwo: '',
        detailLevelThree: '',
        detailLevelFour: '',
        detailLevelFive: '',
        measurementUnitName: '',
        unitsConsumed: '',
        energyKwh: '',
        customMarketBasedMultiplier: '',
        startDate: '',
        endDate: '',
        capitalGoodId: '',
        assignedUserId: '',
        invoiceDate: '',
        total: '',
        bankAccountName: '',
        conversionSourceId: '',
        returnId: '',
        ruleId: '',
        excludeFromReporting: false,
        excludeFromReportingReason: '',
        isFlag: false,
        flagCategory: '',
      },
      validationSchema: editUploadLineBulkSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        if (editCodes.length === 0) {
          userNotificationsContext.openSnackbar({ open: true, message: 'Please select at least one field to edit', backgroundColor: 'text.primary' })
          return
        }
        if (isReportPeriodLocked) {
          if (isForTransactions) {
            userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
            return
          } else if (fileType && (fileType === 'emissions-rules' || fileType === 'emissions-manual' || fileType === 'spend-based')) {
            userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
            return
          }
        }
        if (!isSubmitting && ((dialogDetailsIds && dialogDetailsIds.length > 0) || isEditAll)) {
          setIsSubmitting(true)
          const editCodeToPropertyMap: { [key: string]: string } = {
            'EDIT_SUBSIDIARY': 'subsidiaryId',
            'EDIT_COUNTRY': 'countryId',
            'EDIT_LOCATION': 'locationId',
            'EDIT_SUPPLIER': 'contactSupplierId',
            'EDIT_VEHICLE': 'vehicleId',
            'EDIT_PROJECT': 'projectId',
            'EDIT_DATA_TYPE': 'dataTypeId',
            'EDIT_REGULATED_EMISSION_TRADING_SCHEME': 'regulatedEmissionTradingSchemeId',
            'EDIT_GROUP': 'groupId',
            'EDIT_CATEGORY': 'categoryId',
            'EDIT_UNITS_CONSUMED': 'unitsConsumed',
            'EDIT_ENERGY_KWH': 'energyKwh',
            'EDIT_SUPPLIER_EMISSION_RATE': 'customMarketBasedMultiplier',
            'EDIT_START_DATE': 'startDate',
            'EDIT_END_DATE': 'endDate',
            'EDIT_PRODUCT': 'capitalGoodId',
            'EDIT_ASSIGNED_USER': 'assignedUserId',
            'EDIT_INVOICE_DATE': 'invoiceDate',
            'EDIT_TOTAL': 'total',
            'EDIT_BANK_ACCOUNT_NAME': 'bankAccountName',
            'EDIT_CONVERSION': 'groupId',
            'EDIT_ACTIVITY': 'conversionSourceId',
            'EDIT_RULE': 'ruleId',
            'EDIT_EXCLUDE_FROM_REPORTING': 'excludeFromReporting',
          }
          const updatePayload: any = {}
          editCodes.forEach(editCode => {
            const propertyToUpdate = editCodeToPropertyMap[editCode]
            if (propertyToUpdate) {
              const value = values[propertyToUpdate]
              if (propertyToUpdate === 'excludeFromReporting') {
                updatePayload[propertyToUpdate] = value === undefined ? null : value
              } else {
                updatePayload[propertyToUpdate] = (value === '' || value === undefined) ? null : value
              }
            }
            if (editCode === 'EDIT_ACTIVITY') {
              const returnValue = values.returnId
              const fieldName = fileType === 'spend-based' ? 'spendBasedConversionId' : 'returnId'
              updatePayload[fieldName] = (returnValue === '' || returnValue === undefined) ? null : returnValue
            }
            if (editCode === 'EDIT_CONVERSION') {
              const returnValue = values.returnId
              updatePayload.conversionId = (returnValue === '' || returnValue === undefined) ? null : returnValue
              const measurementUnitValue = values.measurementUnitName
              updatePayload.measurementUnitName = (measurementUnitValue === '' || measurementUnitValue === undefined) ? null : measurementUnitValue
              if (fileType === 'emissions-manual') {
                const categoryNameValue = values.categoryName
                updatePayload.categoryName = (categoryNameValue === '' || categoryNameValue === undefined) ? null : categoryNameValue
              } else if (fileType) {
                const categoryValue = values.categoryId
                updatePayload.categoryId = (categoryValue === '' || categoryValue === undefined) ? null : categoryValue
              }
            }
            if (editCode === 'EDIT_EXCLUDE_FROM_REPORTING') {
              const excludeReasonValue = values.excludeFromReportingReason
              updatePayload.excludeFromReportingReason = (excludeReasonValue === '' || excludeReasonValue === undefined) ? null : excludeReasonValue
            }
          })
          if (fileType === 'emissions-product' && updatePayload.startDate && !updatePayload.endDate) {
            updatePayload.endDate = updatePayload.startDate
          }
          const selectedCustomFieldValues = visibleCustomFields
            .filter(cf => editCodes.includes(`${CUSTOM_FIELD_CODE_PREFIX}${cf.id}`))
            .map(cf => ({
              customFieldId: cf.id,
              value: (values[cf.id] === '' || values[cf.id] === undefined) ? null : values[cf.id],
            }))
          if (selectedCustomFieldValues.length > 0) {
            updatePayload.customFieldValues = selectedCustomFieldValues
          }
          // FLAG is not an emission column, so it never lands in updatePayload — carry it separately.
          const flagPayload = editCodes.includes('EDIT_FLAG_CLASSIFICATION')
            ? { isFlag: !!values.isFlag, flagCategory: values.isFlag ? (values.flagCategory || null) : null }
            : null
          if (Object.keys(updatePayload).length === 0 && !flagPayload) {
            userNotificationsContext.openSnackbar({ open: true, message: 'No valid fields to update', backgroundColor: 'text.primary' })
            setIsSubmitting(false)
            return
          }
          if (isBulkCalculationEdit) {
            if (selectedCalculationEditCodes.length !== editCodes.length) {
              userNotificationsContext.openSnackbar({ open: true, message: 'Calculation fields must be edited separately from emission details', backgroundColor: 'text.primary' })
              setIsSubmitting(false)
              return
            }
            if (selectedCalculationEditCodes.length > 1) {
              userNotificationsContext.openSnackbar({ open: true, message: 'Please edit one calculation field at a time', backgroundColor: 'text.primary' })
              setIsSubmitting(false)
              return
            }

            const calculationPayload = selectedCalculationEditCodes[0] === 'EDIT_ACTIVITY'
              ? {
                type: 'SPEND',
                conversionSourceId: updatePayload.conversionSourceId,
                spendBasedConversionId: updatePayload.returnId,
              }
              : {
                type: 'ACTIVITY',
                groupId: values.groupId,
                categoryId: values.categoryId,
                conversionSourceId: values.conversionSourceId,
                returnId: values.returnId || null,
                detailLevelOne: values.detailLevelOne || null,
                detailLevelTwo: values.detailLevelTwo || null,
                detailLevelThree: values.detailLevelThree || null,
                detailLevelFour: values.detailLevelFour || null,
                detailLevelFive: values.detailLevelFive || null,
                measurementUnitId: values.measurementUnitId || null,
                calendarYear: reportPeriodDates.startDate ? moment(reportPeriodDates.startDate).format('YYYY') : null,
              }

            const calculationPromise = isEditAll && reportPeriodId
              ? emissionCalculationService.bulkEditAllEmissionCalculations(reportPeriodId, calculationPayload, queryString)
              : emissionCalculationService.bulkEditSelectedEmissionCalculations(dialogDetailsIds, calculationPayload)
            calculationPromise
              .then((response) => {
                successHandler()
                setIsSubmitting(false)
                const skippedCount = response.skipped?.length || 0
                const message = skippedCount > 0
                  ? `Updated ${response.updatedEmissions} emissions. Skipped ${skippedCount} unsupported emissions.`
                  : 'Updated successfully'
                userNotificationsContext.openSnackbar({ open: true, message })
              })
              .catch(err => {
                const message = err.response?.data?.message || 'There was an error, please try again'
                userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
                console.log(err)
                setIsSubmitting(false)
              })
            return
          }
          if (isForTransactions) {
            const hasEmissionFields = Object.keys(updatePayload).length > 0
            const emissionPromise = !hasEmissionFields
              ? Promise.resolve()
              : isEditAll && reportPeriodId
                ? emissionService.editAllEmissions(reportPeriodId, updatePayload, queryString)
                : emissionService.editSelectedEmissions(dialogDetailsIds, updatePayload)
            emissionPromise
              .then(() => {
                if (!flagPayload) return
                return isEditAll && reportPeriodId
                  ? emissionCalculationService.updateAllFlagClassification(reportPeriodId, flagPayload, queryString)
                  : emissionCalculationService.updateSelectedFlagClassification(dialogDetailsIds, flagPayload)
              })
              .then(() => {
                successHandler()
                setIsSubmitting(false)
                userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' })
              })
              .catch(err => {
                if (err.response?.data?.code === 1) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
                } else {
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
                }
                console.log(err)
                setIsSubmitting(false)
              })
          } else if (fileType) {
            const editPromise = isEditAll && fileUploadId
              ? fileUploadAllLineService.editAllFileUploadLines(fileUploadId, updatePayload, fileType, create)
              : fileUploadAllLineService.editManyFileUploadLines(dialogDetailsIds, updatePayload, fileType, create)
            editPromise
              .then(() => {
                successHandler()
                setIsSubmitting(false)
                userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' })
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
                console.log(err)
                setIsSubmitting(false)
              })
          }
        }
      }
    })

    useEffect(() => {
      if (fileType === 'emissions-manual' && editCodes.includes('EDIT_CONVERSION')) {
        const group = groups?.find(g => g.id === formik.values.groupId)
        const categoryId = formik.values.categoryId
        if (group && categoryId) {
          conversionSourceService.getConversionSourceForCategory(categoryId, 'conversion', group.scope!, true)
            .then(response => {
              setConversionSources(response)
            })
            .catch(err => console.log(err))
        } else {
          setConversionSources(undefined)
          formik.setFieldValue('conversionSourceId', '')
          formik.setFieldValue('returnId', '')
        }
      }
    }, [fileType, editCodes, groups, formik.values.groupId, formik.values.categoryId, conversionSourceService])

    useEffect(() => {
      if (open) {
        setEditCodes([])
        formik.resetForm({
          values: {
            subsidiaryId: '',
            countryId: '',
            locationId: '',
            contactSupplierId: '',
            vehicleId: '',
            projectId: '',
            dataTypeId: '',
            regulatedEmissionTradingSchemeId: '',
            groupId: '',
            categoryId: '',
            categoryName: '',
            detailLevelOne: '',
            detailLevelTwo: '',
            detailLevelThree: '',
            detailLevelFour: '',
            detailLevelFive: '',
            measurementUnitName: '',
            unitsConsumed: '',
            energyKwh: '',
            customMarketBasedMultiplier: '',
            startDate: '',
            endDate: '',
            capitalGoodId: '',
            assignedUserId: '',
            invoiceDate: '',
            total: '',
            bankAccountName: '',
            conversionSourceId: '',
            returnId: '',
            ruleId: '',
          }
        })
      }
    }, [open])

    const getReportPeriodDates = () => {
      if (reportPeriodId && reportPeriodContext.allReportPeriods) {
        const reportPeriod = reportPeriodContext.allReportPeriods.find(rp => rp.id === reportPeriodId)
        if (reportPeriod) {
          return {
            startDate: reportPeriod.startDate,
            endDate: reportPeriod.endDate,
          }
        }
      }
      return { startDate: undefined, endDate: undefined }
    }

    const reportPeriodDates = getReportPeriodDates()

    const shouldHideMenuItem = (editCode: string) => {
      if (!showMenuItems.includes(editCode)) return true
      if (editCode === 'EDIT_PROJECT' && !organisation?.enableProjects) return true
      if (editCode === 'EDIT_SUBSIDIARY' && !organisation?.enableSubsidiaries) return true
      if (isFieldHidden(editCode)) return true
      return false
    }

    const excludeFromReportingHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'true';
      formik.setFieldValue('excludeFromReporting', value)
      if (!value) {
        formik.setFieldValue('excludeFromReportingReason', '')
      }
    }

    const isFlagHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'true';
      formik.setFieldValue('isFlag', value)
      if (!value) {
        formik.setFieldValue('flagCategory', '')
      }
    }

    const handleCheckboxChange = (editCode: string, event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      setEditCodes(prev => {
        const editCodeToPropertyMap: { [key: string]: string } = {
          'EDIT_SUBSIDIARY': 'subsidiaryId',
          'EDIT_COUNTRY': 'countryId',
          'EDIT_LOCATION': 'locationId',
          'EDIT_SUPPLIER': 'contactSupplierId',
          'EDIT_VEHICLE': 'vehicleId',
          'EDIT_PROJECT': 'projectId',
          'EDIT_DATA_TYPE': 'dataTypeId',
          'EDIT_REGULATED_EMISSION_TRADING_SCHEME': 'regulatedEmissionTradingSchemeId',
          'EDIT_GROUP': 'groupId',
          'EDIT_CATEGORY': 'categoryId',
          'EDIT_UNITS_CONSUMED': 'unitsConsumed',
          'EDIT_ENERGY_KWH': 'energyKwh',
          'EDIT_SUPPLIER_EMISSION_RATE': 'customMarketBasedMultiplier',
          'EDIT_START_DATE': 'startDate',
          'EDIT_END_DATE': 'endDate',
          'EDIT_PRODUCT': 'capitalGoodId',
          'EDIT_ASSIGNED_USER': 'assignedUserId',
          'EDIT_INVOICE_DATE': 'invoiceDate',
          'EDIT_TOTAL': 'total',
          'EDIT_BANK_ACCOUNT_NAME': 'bankAccountName',
          'EDIT_CONVERSION': 'groupId',
          'EDIT_ACTIVITY': 'conversionSourceId',
          'EDIT_RULE': 'ruleId',
        }
        if (prev.includes(editCode)) {
          const propertyName = editCodeToPropertyMap[editCode]
          if (propertyName) {
            formik.setFieldValue(propertyName, '')
          }
          const customFieldId = customFieldIdFromCode(editCode)
          if (customFieldId) {
            formik.setFieldValue(customFieldId, '')
          }
          if (editCode === 'EDIT_ACTIVITY' || editCode === 'EDIT_CONVERSION') {
            formik.setFieldValue('returnId', '')
          }
          if (editCode === 'EDIT_FLAG_CLASSIFICATION') {
            formik.setFieldValue('isFlag', false)
            formik.setFieldValue('flagCategory', '')
          }
          return prev.filter(code => code !== editCode)
        } else {
          return [...prev, editCode]
        }
      })
    }


    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'))
      } else {
        formik.setFieldValue(name, '')
      }
    }

    const conversionSourceHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('conversionSourceId', e.target.value)
      if (e.target.value !== formik.values.conversionSourceId) {
        formik.setFieldValue('returnId', '')
        const year = moment(formik.values.invoiceDate || undefined).format('YYYY')
        spendBasedConversionService.getSpendBasedConversionsForConversionSource(e.target.value, year, true, true)
          .then(response => {
            setSpendBasedConversions(response)
          })
          .catch(err => console.log(err))
      }
    }


    const cancelHandler = () => {
      closeHandler()
    }

    const shouldShowField = (fieldCode: string) => {
      return editCodes.includes(fieldCode)
    }

    return (
      <>
        <DialogWrapper
          open={open}
          onClose={closeHandler}
          title="Edit Selected"
          cancelHandler={cancelHandler}
          submitHandler={formik.submitForm}
          isSubmitting={isSubmitting}
          maxWidth="md"
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              height: 460,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flex: '0 0 250px',
                p: 2,
                backgroundColor: '#fafafa',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', flexShrink: 0 }}>
                Select Fields to Edit
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  overflowY: 'auto',
                  paddingRight: '8px',
                  height: 'calc(100% - 8px)',
                  pb: 6,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  }
                }}
              >
                <Stack
                  spacing={1}
                >
                  {[
                    { code: 'EDIT_ACTIVITY', visible: showMenuItems.includes('EDIT_ACTIVITY'), label: calculationEditCodes.includes('EDIT_ACTIVITY') ? 'Category (Spend)' : 'Activity' },
                    { code: 'EDIT_ASSIGNED_USER', visible: showMenuItems.includes('EDIT_ASSIGNED_USER'), label: 'Assigned User' },
                    { code: 'EDIT_BANK_ACCOUNT_NAME', visible: showMenuItems.includes('EDIT_BANK_ACCOUNT_NAME'), label: 'Bank Account Name' },
                    { code: 'EDIT_CATEGORY', visible: showMenuItems.includes('EDIT_CATEGORY'), label: 'Category' },
                    { code: 'EDIT_CONVERSION', visible: showMenuItems.includes('EDIT_CONVERSION'), label: calculationEditCodes.includes('EDIT_CONVERSION') ? 'Category' : 'Conversion' },
                    { code: 'EDIT_COUNTRY', visible: showMenuItems.includes('EDIT_COUNTRY') && !isFieldHidden('EDIT_COUNTRY'), label: 'Country' },
                    { code: 'EDIT_DATA_TYPE', visible: showMenuItems.includes('EDIT_DATA_TYPE'), label: 'Data Type' },
                    { code: 'EDIT_ENERGY_KWH', visible: showMenuItems.includes('EDIT_ENERGY_KWH'), label: 'Energy Consumption' },
                    { code: 'EDIT_END_DATE', visible: showMenuItems.includes('EDIT_END_DATE'), label: 'End Date' },
                    { code: 'EDIT_EXCLUDE_FROM_REPORTING', visible: showMenuItems.includes('EDIT_EXCLUDE_FROM_REPORTING') && !isFieldHidden('EDIT_EXCLUDE_FROM_REPORTING'), label: 'Exclude from Reporting' },
                    { code: 'EDIT_FLAG_CLASSIFICATION', visible: showMenuItems.includes('EDIT_FLAG_CLASSIFICATION') && canEditFlagClassification, label: 'FLAG classification' },
                    { code: 'EDIT_GROUP', visible: showMenuItems.includes('EDIT_GROUP'), label: 'Group' },
                    { code: 'EDIT_INVOICE_DATE', visible: showMenuItems.includes('EDIT_INVOICE_DATE'), label: 'Invoice Date' },
                    { code: 'EDIT_LOCATION', visible: showMenuItems.includes('EDIT_LOCATION') && !isFieldHidden('EDIT_LOCATION'), label: screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' }) },
                    { code: 'EDIT_PRODUCT', visible: showMenuItems.includes('EDIT_PRODUCT') && !isFieldHidden('EDIT_PRODUCT'), label: 'Product' },
                    { code: 'EDIT_PROJECT', visible: !shouldHideMenuItem('EDIT_PROJECT'), label: screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }) },
                    { code: 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', visible: showMenuItems.includes('EDIT_REGULATED_EMISSION_TRADING_SCHEME') && !isFieldHidden('EDIT_REGULATED_EMISSION_TRADING_SCHEME'), label: 'Regulated Emission Trading Scheme' },
                    { code: 'EDIT_RULE', visible: showMenuItems.includes('EDIT_RULE'), label: 'Rule' },
                    { code: 'EDIT_START_DATE', visible: showMenuItems.includes('EDIT_START_DATE'), label: fileType === 'emissions-product' ? 'Date Purchased' : 'Start Date' },
                    { code: 'EDIT_SUBSIDIARY', visible: !shouldHideMenuItem('EDIT_SUBSIDIARY'), label: screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' }) },
                    { code: 'EDIT_SUPPLIER', visible: showMenuItems.includes('EDIT_SUPPLIER') && !isFieldHidden('EDIT_SUPPLIER'), label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }) },
                    { code: 'EDIT_SUPPLIER_EMISSION_RATE', visible: showMenuItems.includes('EDIT_SUPPLIER_EMISSION_RATE'), label: 'Supplier Emission Rate' },
                    { code: 'EDIT_TOTAL', visible: showMenuItems.includes('EDIT_TOTAL'), label: 'Total' },
                    { code: 'EDIT_UNITS_CONSUMED', visible: showMenuItems.includes('EDIT_UNITS_CONSUMED'), label: fileType === 'emissions-product' ? 'Number of Units Purchased' : 'Units Consumed' },
                    { code: 'EDIT_VEHICLE', visible: showMenuItems.includes('EDIT_VEHICLE') && !isFieldHidden('EDIT_VEHICLE'), label: screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' }) },
                    ...visibleCustomFields.map(cf => ({ code: `${CUSTOM_FIELD_CODE_PREFIX}${cf.id}`, visible: true, label: cf.fieldName })),
                  ]
                    .filter(item => item.visible)
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map(item => {
                      const isSelected = editCodes.includes(item.code);
                      return (
                        <FormControlLabel
                          key={item.code}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleCheckboxChange(item.code, e)}
                              sx={{
                                color: 'inherit',
                                '&.Mui-checked': {
                                  color: 'inherit',
                                },
                              }}
                            />
                          }
                          label={item.label}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            mx: 0,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: isSelected ? 'primary.main' : 'transparent',
                            color: isSelected ? 'white' : 'text.primary',
                            '&:hover': {
                              backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        />
                      );
                    })}
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {editCodes.length === 0 && (
                <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                  {calculationEditCodes.length > 0
                    ? 'Pick fields from the left menu to edit emission records. Category and spend category changes will recalculate matching calculations.'
                    : isForTransactions
                    ? 'Pick fields from the left menu to edit the emission records.'
                    : 'Pick fields from the left menu to choose what to update for the selected records.'}
                </Alert>
              )}
              {shouldShowField('EDIT_ACTIVITY') && conversionSources &&
                <>
                  <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                    <InputLabel htmlFor='conversionSourceId'>{calculationEditCodes.includes('EDIT_ACTIVITY') ? 'Spend Category Source' : 'Conversion Source'}</InputLabel>
                    <Select
                      id='conversionSourceId'
                      label={calculationEditCodes.includes('EDIT_ACTIVITY') ? 'Spend Category Source' : 'Conversion Source'}
                      name='conversionSourceId'
                      value={formik.values.conversionSourceId || ''}
                      onChange={conversionSourceHandler}
                      error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                      sx={{ color: 'text.primary' }}
                    >
                      {conversionSources.filter(source => source.conversionType === 'spendBasedConversion').sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) =>
                        <MenuItem key={index} value={conversionSource.id}>{conversionSource.source}</MenuItem>
                      )}
                    </Select>
                    {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors.conversionSourceId ? <div>{formik.errors.conversionSourceId as string}</div> : null}
                      </FormHelperText>
                    }
                  </FormControl>
                  <FormControl fullWidth variant='outlined' sx={{ flexBasis: '200px', mt: 2 }}>
                    <Autocomplete
                      id="returnId"
                      options={sortedSpendBasedConversions}
                      freeSolo={false}
                      autoHighlight
                      disabled={!spendBasedConversions || spendBasedConversions.length === 0}
                      getOptionLabel={(option) => typeof option === 'string' ? option : formatConversionCodeLabel(option)}
                      renderOption={(props, option) => {
                        const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                        return (
                          <Box
                            component="li"
                            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                            key={key}
                            {...restProps}
                          >
                            {formatConversionCodeLabel(option, ' -')}
                          </Box>
                        )
                      }}
                      value={spendBasedConversions?.find(conversion => conversion.id === formik.values.returnId) || null}
                      onChange={(event, newValue) => {
                        formik.setFieldValue('returnId', typeof newValue === 'string' ? '' : newValue?.id || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={{ color: 'text.primary' }}
                          label={calculationEditCodes.includes('EDIT_ACTIVITY') ? 'Spend Category' : 'Activity'}
                          error={formik.touched.returnId && Boolean(formik.errors.returnId)}
                          helperText={formik.touched.returnId && formik.errors.returnId ? formik.errors.returnId as string : null}
                        />
                      )}
                    />
                  </FormControl>
                </>}
              {shouldShowField('EDIT_ASSIGNED_USER') && users &&
                <Autocomplete
                  id='assignedUserId'
                  options={users.sort((a, b) => a.firstName.localeCompare(b.firstName))}
                  getOptionLabel={(option) => `${option.firstName} ${option.surname}`}
                  value={formik.values.assignedUserId ? users.find(u => u.id === formik.values.assignedUserId) || null : null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('assignedUserId', newValue?.id || '')
                  }}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options
                    const searchLower = inputValue.toLowerCase()
                    return options.filter(option => {
                      const fullName = `${option.firstName} ${option.surname}`.toLowerCase()
                      return fullName.includes(searchLower)
                    })
                  }}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box component="li" key={key} {...restProps}>
                        {`${option.firstName} ${option.surname}`}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Assigned User'
                      error={formik.touched.assignedUserId && Boolean(formik.errors.assignedUserId)}
                      helperText={formik.touched.assignedUserId && formik.errors.assignedUserId ? (formik.errors.assignedUserId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_BANK_ACCOUNT_NAME') && (
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='bankAccountName'>Bank Account Name</InputLabel>
                  <OutlinedInput
                    id='bankAccountName'
                    label='Bank Account Name'
                    name='bankAccountName'
                    type='text'
                    value={formik.values.bankAccountName || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.bankAccountName && Boolean(formik.errors.bankAccountName)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.bankAccountName && formik.errors.bankAccountName &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.bankAccountName ? <div>{formik.errors.bankAccountName as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              )}
              {shouldShowField('EDIT_CATEGORY') && categories &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='categoryId'>Category</InputLabel>
                  <Select
                    id='categoryId'
                    label='Category'
                    name='categoryId'
                    value={formik.values.categoryId || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                    sx={{ color: 'text.primary' }}
                  >
                    {categories.sort((a, b) => a.name.localeCompare(b.name)).map((category, index) =>
                      <MenuItem key={index} value={category.id}>{getCategoryDisplayName(category)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.categoryId && formik.errors.categoryId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.categoryId ? <div>{formik.errors.categoryId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_CONVERSION') &&
                <>
                  {/* The source list is fetched for the chosen group and category, so asking
                      for it first offered an empty dropdown. It follows them now. */}
                  {groups &&
                    <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                      <InputLabel htmlFor='groupId-conversion'>Group</InputLabel>
                      <Select
                        id='groupId-conversion'
                        label='Group'
                        name='groupId'
                        value={formik.values.groupId || ''}
                        onChange={(e) => {
                          const value = e.target.value
                          formik.setFieldValue('groupId', value)
                          formik.setFieldValue('categoryId', '')
                          formik.setFieldValue('categoryName', '')
                          formik.setFieldValue('returnId', '')
                          formik.setFieldValue('detailLevelOne', '')
                          formik.setFieldValue('detailLevelTwo', '')
                          formik.setFieldValue('detailLevelThree', '')
                          formik.setFieldValue('detailLevelFour', '')
                          formik.setFieldValue('detailLevelFive', '')
                          formik.setFieldValue('measurementUnitName', '')
                          formik.setFieldValue('measurementUnitId', '')
                        }}
                        error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                        sx={{ color: 'text.primary' }}
                      >
                        {(() => {
                          const groupsByScope = groups.reduce((acc, group) => {
                            const scope = group.scope || 'Other';
                            if (!acc[scope]) {
                              acc[scope] = [];
                            }
                            acc[scope].push(group);
                            return acc;
                          }, {} as Record<string, typeof groups>);

                          const scopes = ['1', '2', '3'].filter(scope => groupsByScope[scope]);
                          const otherScopes = Object.keys(groupsByScope).filter(scope => !['1', '2', '3'].includes(scope));
                          const allScopes = [...scopes, ...otherScopes.sort()];

                          return allScopes.flatMap((scope) => [
                            <ListSubheader key={`header-conversion-${scope}`}>Scope {scope}</ListSubheader>,
                            ...sortGroupsByGhgCategory(groupsByScope[scope])
                              .map((group, index) => (
                                <MenuItem key={`conversion-${scope}-${index}-${group.id}`} value={group.id}>
                                  {formatGroupLabel(group, { includeScope: false })}
                                </MenuItem>
                              ))
                          ]);
                        })()}
                      </Select>
                      {formik.touched.groupId && formik.errors.groupId &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                          {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
                        </FormHelperText>
                      }
                    </FormControl>
                  }
                  {groups &&
                    <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                      <InputLabel htmlFor='categoryId-conversion'>Category</InputLabel>
                      <Select
                        id='categoryId-conversion'
                        label='Category'
                        name='categoryId'
                        value={formik.values.categoryId || ''}
                        onChange={(e) => {
                          const value = e.target.value
                          formik.setFieldValue('categoryId', value)
                          const selectedGroup = groups.find(g => g.id === formik.values.groupId)
                          const selectedCategory = selectedGroup?.categories.find(c => c.id === value)
                          formik.setFieldValue('categoryName', selectedCategory ? selectedCategory.name : '')
                          formik.setFieldValue('returnId', '')
                          formik.setFieldValue('detailLevelOne', '')
                          formik.setFieldValue('detailLevelTwo', '')
                          formik.setFieldValue('detailLevelThree', '')
                          formik.setFieldValue('detailLevelFour', '')
                          formik.setFieldValue('detailLevelFive', '')
                          formik.setFieldValue('measurementUnitName', '')
                          formik.setFieldValue('measurementUnitId', '')
                        }}
                        disabled={!formik.values.groupId}
                        sx={{ color: 'text.primary' }}
                      >
                        {groups.find(g => g.id === formik.values.groupId)?.categories
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((category, index) =>
                            <MenuItem key={index} value={category.id}>{getCategoryDisplayName(category)}</MenuItem>
                          )}
                      </Select>
                    </FormControl>
                  }
                  <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                    <InputLabel htmlFor='conversionSourceId-conversion'>Conversion Source</InputLabel>
                    <Select
                      id='conversionSourceId-conversion'
                      label='Conversion Source'
                      name='conversionSourceId'
                      value={formik.values.conversionSourceId || ''}
                      onChange={(e) => {
                        formik.setFieldValue('conversionSourceId', e.target.value)
                        formik.setFieldValue('returnId', '')
                        formik.setFieldValue('detailLevelOne', '')
                        formik.setFieldValue('detailLevelTwo', '')
                        formik.setFieldValue('detailLevelThree', '')
                        formik.setFieldValue('detailLevelFour', '')
                        formik.setFieldValue('detailLevelFive', '')
                        formik.setFieldValue('measurementUnitName', '')
                        formik.setFieldValue('measurementUnitId', '')
                      }}
                      disabled={!conversionSources}
                      error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                      sx={{ color: 'text.primary' }}
                    >
                      {(conversionSources || []).filter(source => source.conversionType === 'conversion').sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) =>
                        <MenuItem key={index} value={conversionSource.id}>{conversionSource.source}</MenuItem>
                      )}
                    </Select>
                    {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors.conversionSourceId ? <div>{formik.errors.conversionSourceId as string}</div> : null}
                      </FormHelperText>
                    }
                  </FormControl>
                  {formik.values.categoryId && formik.values.groupId && formik.values.conversionSourceId && reportPeriodDates.startDate &&
                    <Stack sx={{ mt: 2 }}>
                      <EmissionsFilterLogic
                        categoryId={formik.values.categoryId}
                        groupId={formik.values.groupId}
                        calendarYear={moment(reportPeriodDates.startDate).format('YYYY')}
                        defaultConversionSourceId={formik.values.conversionSourceId}
                        inputValues={{
                          detailLevelOne: formik.values.detailLevelOne,
                          detailLevelTwo: formik.values.detailLevelTwo,
                          detailLevelThree: formik.values.detailLevelThree,
                          detailLevelFour: formik.values.detailLevelFour,
                          detailLevelFive: formik.values.detailLevelFive,
                          measurementUnitName: formik.values.measurementUnitName,
                        }}
                        formik={formik}
                        isEdit={!calculationEditCodes.includes('EDIT_CONVERSION')}
                        direction='column'
                      />
                    </Stack>
                  }
                  {(!formik.values.conversionSourceId || !formik.values.groupId || !formik.values.categoryId || !reportPeriodDates.startDate) &&
                    <Stack sx={{ mt: 2 }}>
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel htmlFor='additionalInformation'>Factor Detail 1</InputLabel>
                        <Select
                          id='additionalInformation'
                          label='Factor Detail 1'
                          disabled={true}>
                        </Select>
                      </FormControl>
                    </Stack>}
                </>}
              {shouldShowField('EDIT_COUNTRY') && countries &&
                <Autocomplete
                  id='countryId'
                  options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
                  getOptionLabel={(option) => option.name}
                  value={countries.find(c => c.id === formik.values.countryId) || null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('countryId', newValue?.id || '')
                  }}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box
                        component="li"
                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                        key={key}
                        {...restProps}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={option.imageUrl}
                          alt="country flag"
                        />
                        {option.name}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Country'
                      error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                      helperText={formik.touched.countryId && formik.errors.countryId ? (formik.errors.countryId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_DATA_TYPE') && dataTypes &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='dataTypeId'>Data Type</InputLabel>
                  <Select
                    id='dataTypeId'
                    label='Data Type'
                    name='dataTypeId'
                    value={formik.values.dataTypeId || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.dataTypeId && Boolean(formik.errors.dataTypeId)}
                    sx={{ color: 'text.primary' }}
                  >
                    {dataTypes.map((dataType, index) =>
                      <MenuItem key={index} value={dataType.id}>{dataType.dataType}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.dataTypeId && formik.errors.dataTypeId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.dataTypeId ? <div>{formik.errors.dataTypeId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_ENERGY_KWH') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='energyKwh'>Energy Consumption</InputLabel>
                  <OutlinedInput
                    inputProps={{ min: 0 }}
                    id='energyKwh'
                    label='Energy Consumption'
                    name='energyKwh'
                    type='number'
                    value={formik.values.energyKwh || ''}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position='end'>kWh</InputAdornment>}
                    error={formik.touched.energyKwh && Boolean(formik.errors.energyKwh)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.energyKwh && formik.errors.energyKwh &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.energyKwh ? <div>{formik.errors.energyKwh as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_END_DATE') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label='End Date'
                      minDate={formik.values.startDate ? moment(formik.values.startDate) : (reportPeriodDates.startDate ? moment(reportPeriodDates.startDate) : undefined)}
                      maxDate={reportPeriodDates.endDate ? moment(reportPeriodDates.endDate) : undefined}
                      value={formik.values.endDate ? moment(formik.values.endDate) : null}
                      onChange={(date) => dateHandler(date, 'endDate')}
                      format='DD/MM/YYYY'
                      sx={{ color: 'text.primary' }}
                      open={openEndDate}
                      onOpen={() => setOpenEndDate(true)}
                      onClose={() => setOpenEndDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenEndDate(true),
                          error: formik.touched.endDate && Boolean(formik.errors.endDate)
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.endDate && formik.errors.endDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_EXCLUDE_FROM_REPORTING') &&
                <>
                  <FormControl>
                    <Typography variant='body1' sx={{ color: 'text.primary', mt: 2 }}>Do you wish to exclude these emissions from analytics and reporting? </Typography>
                    <RadioGroup
                      onChange={excludeFromReportingHandler}
                      value={formik.values.excludeFromReporting}
                      sx={{
                        width: '100%',
                        display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start'
                      }}>
                      <FormControlLabel value={true} control={<Radio />} label='Yes' />
                      <FormControlLabel value={false} control={<Radio />} label='No' />
                    </RadioGroup>
                  </FormControl>
                  {formik.values.excludeFromReporting &&
                    <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                      <InputLabel htmlFor='excludeFromReportingReason'>Reason for Exclusion</InputLabel>
                      <OutlinedInput
                        id='excludeFromReportingReason'
                        label='Reason for exclusion'
                        name='excludeFromReportingReason'
                        value={formik.values.excludeFromReportingReason || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.excludeFromReportingReason && Boolean(formik.errors.excludeFromReportingReason)}
                        sx={{ color: 'text.primary' }}
                      />
                      {formik.touched.excludeFromReportingReason && formik.errors.excludeFromReportingReason &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                          {formik.errors.excludeFromReportingReason ? <div>{formik.errors.excludeFromReportingReason as string}</div> : null}
                        </FormHelperText>
                      }
                    </FormControl>
                  }
                </>
              }
              {shouldShowField('EDIT_FLAG_CLASSIFICATION') &&
                <>
                  <FormControl>
                    <Typography variant='body1' sx={{ color: 'text.primary', mt: 2 }}>Are these FLAG-related calculations? </Typography>
                    <RadioGroup
                      onChange={isFlagHandler}
                      value={formik.values.isFlag}
                      sx={{
                        width: '100%',
                        display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start'
                      }}>
                      <FormControlLabel value={true} control={<Radio />} label='Yes' />
                      <FormControlLabel value={false} control={<Radio />} label='No' />
                    </RadioGroup>
                  </FormControl>
                  {formik.values.isFlag &&
                    <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                      <InputLabel htmlFor='flagCategory'>FLAG category</InputLabel>
                      <Select
                        id='flagCategory'
                        label='FLAG category'
                        name='flagCategory'
                        value={formik.values.flagCategory || ''}
                        onChange={formik.handleChange}
                        sx={{ color: 'text.primary' }}
                      >
                        <MenuItem value=''>None</MenuItem>
                        {FLAG_CATEGORY_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  }
                </>
              }
              {groups && shouldShowField('EDIT_GROUP') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupId'>Group</InputLabel>
                  <Select
                    id='groupId'
                    label='Group'
                    name='groupId'
                    value={formik.values.groupId || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                    sx={{ color: 'text.primary' }}
                  >
                    {(() => {
                      const groupsByScope = groups.reduce((acc, group) => {
                        const scope = group.scope || 'Other';
                        if (!acc[scope]) {
                          acc[scope] = [];
                        }
                        acc[scope].push(group);
                        return acc;
                      }, {} as Record<string, typeof groups>);

                      const scopes = ['1', '2', '3'].filter(scope => groupsByScope[scope]);
                      const otherScopes = Object.keys(groupsByScope).filter(scope => !['1', '2', '3'].includes(scope));
                      const allScopes = [...scopes, ...otherScopes.sort()];

                      return allScopes.flatMap((scope) => [
                        <ListSubheader key={`header-${scope}`}>Scope {scope}</ListSubheader>,
                        ...sortGroupsByGhgCategory(groupsByScope[scope])
                          .map((group, index) => (
                            <MenuItem key={`${scope}-${index}-${group.id}`} value={group.id}>
                              {formatGroupLabel(group, { includeScope: false })}
                            </MenuItem>
                          ))
                      ]);
                    })()}
                  </Select>
                  {formik.touched.groupId && formik.errors.groupId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_INVOICE_DATE') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label='Invoice Date'
                      minDate={reportPeriodDates.startDate ? moment(reportPeriodDates.startDate) : undefined}
                      maxDate={reportPeriodDates.endDate ? moment(reportPeriodDates.endDate) : undefined}
                      value={formik.values.invoiceDate ? moment(formik.values.invoiceDate) : null}
                      onChange={(date) => dateHandler(date, 'invoiceDate')}
                      format='DD/MM/YYYY'
                      sx={{ color: 'text.primary' }}
                      open={openInvoiceDate}
                      onOpen={() => setOpenInvoiceDate(true)}
                      onClose={() => setOpenInvoiceDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenInvoiceDate(true),
                          error: formik.touched.invoiceDate && Boolean(formik.errors.invoiceDate)
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.invoiceDate && formik.errors.invoiceDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.invoiceDate ? <div>{formik.errors.invoiceDate as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_LOCATION') && locations &&
                <Autocomplete
                  id='locationId'
                  options={locations.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                  getOptionLabel={(option) => option.friendlyName}
                  value={formik.values.locationId ? locations.find(l => l.id === formik.values.locationId) || null : null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('locationId', newValue?.id || '')
                  }}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box component="li" key={key} {...restProps}>
                        {option.friendlyName}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}
                      error={formik.touched.locationId && Boolean(formik.errors.locationId)}
                      helperText={formik.touched.locationId && formik.errors.locationId ? (formik.errors.locationId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_PRODUCT') && capitalGoods &&
                <Autocomplete
                  id='capitalGoodId'
                  options={capitalGoods.sort((a, b) => a.name.localeCompare(b.name))}
                  getOptionLabel={(option) => option.name}
                  value={capitalGoods.find(g => g.id === formik.values.capitalGoodId) || null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('capitalGoodId', newValue?.id || '')
                  }}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options
                    const searchLower = inputValue.toLowerCase()
                    return options.filter(option => option.name.toLowerCase().includes(searchLower))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Product'
                      error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)}
                      helperText={formik.touched.capitalGoodId && formik.errors.capitalGoodId ? (formik.errors.capitalGoodId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_PROJECT') && projects &&
                <Autocomplete
                  id='projectId'
                  options={projects.sort((a, b) => (a.summary || '').localeCompare(b.summary || ''))}
                  getOptionLabel={(option) => option.summary || ''}
                  value={formik.values.projectId ? projects.find(p => p.id === formik.values.projectId) || null : null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('projectId', newValue?.id || '')
                  }}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options
                    const searchLower = inputValue.toLowerCase()
                    return options.filter(option => {
                      return (option.summary || '').toLowerCase().includes(searchLower)
                    })
                  }}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box component="li" key={key} {...restProps}>
                        {option.summary || ''}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })}
                      error={formik.touched.projectId && Boolean(formik.errors.projectId)}
                      helperText={formik.touched.projectId && formik.errors.projectId ? (formik.errors.projectId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_REGULATED_EMISSION_TRADING_SCHEME') && regulatedEmissionTradingSchemes &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='regulatedEmissionTradingSchemeId'>Regulated Emission Trading Scheme</InputLabel>
                  <Select
                    id='regulatedEmissionTradingSchemeId'
                    label='Regulated Emission Trading Scheme'
                    name='regulatedEmissionTradingSchemeId'
                    value={formik.values.regulatedEmissionTradingSchemeId || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.regulatedEmissionTradingSchemeId && Boolean(formik.errors.regulatedEmissionTradingSchemeId)}
                    sx={{ color: 'text.primary' }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {regulatedEmissionTradingSchemes.map((scheme, index) =>
                      <MenuItem key={index} value={scheme.id}>{scheme.name}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.regulatedEmissionTradingSchemeId && formik.errors.regulatedEmissionTradingSchemeId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.regulatedEmissionTradingSchemeId ? <div>{formik.errors.regulatedEmissionTradingSchemeId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_RULE') && rules &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='ruleId'>Rule</InputLabel>
                  <Select
                    id='ruleId'
                    label='Rule'
                    name='ruleId'
                    value={formik.values.ruleId || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.ruleId && Boolean(formik.errors.ruleId)}
                    sx={{ color: 'text.primary' }}
                  >
                    {rules.sort((a, b) => a.value.localeCompare(b.value)).map((rule, index) =>
                      <MenuItem key={index} value={rule.id}>{rule.value}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.ruleId && formik.errors.ruleId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.ruleId ? <div>{formik.errors.ruleId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_START_DATE') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label={fileType === 'emissions-product' ? 'Date Purchased' : 'Start Date'}
                      value={formik.values.startDate ? moment(formik.values.startDate) : null}
                      onChange={(date) => dateHandler(date, 'startDate')}
                      format='DD/MM/YYYY'
                      minDate={reportPeriodDates.startDate ? moment(reportPeriodDates.startDate) : undefined}
                      maxDate={formik.values.endDate ? moment(formik.values.endDate) : (reportPeriodDates.endDate ? moment(reportPeriodDates.endDate) : undefined)}
                      sx={{ color: 'text.primary' }}
                      open={openStartDate}
                      onOpen={() => setOpenStartDate(true)}
                      onClose={() => setOpenStartDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenStartDate(true),
                          error: formik.touched.startDate && Boolean(formik.errors.startDate)
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.startDate && formik.errors.startDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_SUBSIDIARY') && subsidiaries &&
                <Autocomplete
                  id='subsidiaryId'
                  options={getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name))}
                  getOptionLabel={(option) => option.name}
                  value={subsidiaries.find(s => s.id === formik.values.subsidiaryId) || null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('subsidiaryId', newValue?.id || '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}
                      error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                      helperText={formik.touched.subsidiaryId && formik.errors.subsidiaryId ? (formik.errors.subsidiaryId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {shouldShowField('EDIT_SUPPLIER') && suppliers &&
                <SupplierPicker
                  id='contactSupplierId'
                  suppliers={suppliers}
                  value={formik.values.contactSupplierId}
                  onChange={(id) => {
                    formik.setFieldValue('contactSupplierId', id || '')
                  }}
                  error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
                  helperText={formik.touched.contactSupplierId && formik.errors.contactSupplierId ? (formik.errors.contactSupplierId as string) : undefined}
                  sx={{ mt: 2 }}
                />
              }
              {shouldShowField('EDIT_SUPPLIER_EMISSION_RATE') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='customMarketBasedMultiplier'>Supplier Emission Rate</InputLabel>
                  <OutlinedInput
                    inputProps={{ min: 0 }}
                    id='customMarketBasedMultiplier'
                    label='Supplier Emission Rate'
                    name='customMarketBasedMultiplier'
                    type='number'
                    value={formik.values.customMarketBasedMultiplier ?? ''}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position='end'>kgCO₂/kWh</InputAdornment>}
                    error={formik.touched.customMarketBasedMultiplier && Boolean(formik.errors.customMarketBasedMultiplier)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.customMarketBasedMultiplier && formik.errors.customMarketBasedMultiplier &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.customMarketBasedMultiplier ? <div>{formik.errors.customMarketBasedMultiplier as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_TOTAL') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='total'>Total</InputLabel>
                  <OutlinedInput
                    inputProps={{ min: 0 }}
                    id='total'
                    label='Total'
                    name='total'
                    type='number'
                    value={formik.values.total || ''}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position='end'>£</InputAdornment>}
                    error={formik.touched.total && Boolean(formik.errors.total)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.total && formik.errors.total &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.total ? <div>{formik.errors.total as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_UNITS_CONSUMED') &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='unitsConsumed'>{fileType === 'emissions-product' ? 'Number of Units Purchased' : 'Units Consumed'}</InputLabel>
                  <OutlinedInput
                    inputProps={{ min: 0 }}
                    id='unitsConsumed'
                    label={fileType === 'emissions-product' ? 'Number of Units Purchased' : 'Units Consumed'}
                    name='unitsConsumed'
                    type='number'
                    value={formik.values.unitsConsumed || ''}
                    onChange={formik.handleChange}
                    endAdornment={fileType === 'emissions-direct' ? <InputAdornment position='end'>kg CO2e</InputAdornment> : undefined}
                    error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
              {shouldShowField('EDIT_VEHICLE') && vehicles &&
                <Autocomplete
                  id='vehicleId'
                  options={vehicles.sort((a, b) => (a.customUniqueId || '').localeCompare(b.customUniqueId || ''))}
                  getOptionLabel={(option) => option.customUniqueId || ''}
                  value={formik.values.vehicleId ? vehicles.find(v => v.id === formik.values.vehicleId) || null : null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('vehicleId', newValue?.id || '')
                  }}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options
                    const searchLower = inputValue.toLowerCase()
                    return options.filter(option => {
                      return (option.customUniqueId || '').toLowerCase().includes(searchLower)
                    })
                  }}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box component="li" key={key} {...restProps}>
                        {option.customUniqueId || ''}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })}
                      error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
                      helperText={formik.touched.vehicleId && formik.errors.vehicleId ? (formik.errors.vehicleId as string) : undefined}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              }
              {visibleCustomFields
                .filter(cf => editCodes.includes(`${CUSTOM_FIELD_CODE_PREFIX}${cf.id}`))
                .map(cf => (
                  <FormControl key={cf.id} fullWidth variant='outlined' sx={{ mt: 2 }}>
                    <InputLabel htmlFor={cf.id}>{cf.fieldName}</InputLabel>
                    <OutlinedInput
                      id={cf.id}
                      label={cf.fieldName}
                      name={cf.id}
                      type={cf.fieldType === 'number' ? 'number' : 'text'}
                      value={formik.values[cf.id] ?? ''}
                      onChange={formik.handleChange}
                      error={formik.touched[cf.id] && Boolean(formik.errors[cf.id])}
                      sx={{ color: 'text.primary' }}
                    />
                    {formik.touched[cf.id] && formik.errors[cf.id] &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors[cf.id] ? <div>{formik.errors[cf.id] as string}</div> : null}
                      </FormHelperText>
                    }
                  </FormControl>
                ))}
            </Box>
          </Box>
        </DialogWrapper>
      </>
    )
  }

